import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { getInvoiceDetail } from "redux/erp/paymentRequestSlice";
import { LuExternalLink, LuMaximize2, LuMinimize2 } from "react-icons/lu";
import { Modal } from "components/common";
import Clickable from "./common/Clickable";
import InvoicePreviewFrame from "./common/InvoicePreviewFrame";

/**
 * Xem nhanh file hoá đơn của 1 phiếu ngay từ danh sách.
 *
 * Mục đích: người duyệt ở tab "Waiting For Approval" cần đối chiếu chứng từ
 * TRƯỚC khi tick chọn ký hàng loạt — nếu phải mở từng phiếu ra trang detail rồi
 * quay lại thì mất luôn lựa chọn đang tick, ký hàng loạt thành vô nghĩa.
 *
 * Dữ liệu file lấy thẳng từ `invoice_files` của list (BE trả sẵn), chỉ gọi API
 * khi cần URL PDF của đúng file được bấm.
 */
const TOOL_BTN = "tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-rounded-geist-sm tw-border tw-border-solid " +
  "tw-border-geist-border tw-bg-white tw-text-[12px] tw-font-medium tw-text-geist-fg-2 tw-no-underline hover:tw-bg-geist-bg-2 hover:tw-text-geist-fg";
const money = (v) => (Number(v) || 0).toLocaleString("en-US");

const InvoiceFilesModal = ({ show, onHide, paymentRequest }) => {
  const dispatch = useDispatch();
  const files = paymentRequest?.invoice_files || [];

  const [activeId, setActiveId] = useState(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  // Full screen: the PDF takes the whole window so the approver can read the invoice
  // lines without opening the request.
  const [expanded, setExpanded] = useState(false);
  const urlCache = useRef({});

  const openFile = useCallback(async (documentId) => {
    if (!documentId) return;
    setActiveId(documentId);

    if (urlCache.current[documentId]) {
      setUrl(urlCache.current[documentId]);
      return;
    }
    setLoading(true);
    setUrl(null);
    try {
      const inv = await dispatch(getInvoiceDetail(documentId)).unwrap();
      const found = inv?.view_url || inv?.download_url || null;
      if (found) urlCache.current[documentId] = found;
      setUrl(found);
    } catch {
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // Mở modal là xem luôn file đầu tiên — đỡ 1 cú click cho thao tác lặp nhiều lần.
  useEffect(() => {
    if (!show) { setActiveId(null); setUrl(null); setLoading(false); setExpanded(false); return; }
    if (files.length > 0) openFile(files[0].document_id);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normal: a comfortable reading height; full screen: everything below the title bar.
  const frameHeight = expanded ? "calc(100dvh - 110px)" : "max(460px, 70dvh)";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      fullscreen={expanded}
      title={(
        <span className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-mr-2">{`Invoice files — ${paymentRequest?.code || ""}`}</span>
          {files.length > 0 && (
            <button type="button" onClick={() => setExpanded((v) => !v)} className={TOOL_BTN}
              aria-label={expanded ? "Exit full screen" : "Full screen"} title={expanded ? "Exit full screen" : "Full screen"}>
              {expanded ? <LuMinimize2 size={14} /> : <LuMaximize2 size={14} />}
              <span>{expanded ? "Exit full screen" : "Full screen"}</span>
            </button>
          )}
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" className={TOOL_BTN} title="Open the PDF in a new tab">
              <LuExternalLink size={14} /><span>New tab</span>
            </a>
          )}
        </span>
      )}
    >
      <div className="geist-scope">
        {files.length === 0 ? (
          <div className="tw-px-4 tw-py-10 tw-text-center">
            <i className="bi bi-file-earmark-x tw-text-geist-fg-3" style={{ fontSize: 28 }} />
            <p className="tw-text-[13px] tw-text-geist-fg-2 tw-m-0 tw-mt-2">No invoice file linked to this request.</p>
            <p className="tw-text-[11px] tw-text-geist-fg-3 tw-m-0 tw-mt-1">
              Only lines picked from the Document System carry a PDF.
            </p>
          </div>
        ) : (
          <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-3" style={{ height: frameHeight }}>
            {/* Danh sách file */}
            <div className="lg:tw-w-64 tw-flex-shrink-0 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm tw-overflow-hidden tw-divide-y tw-divide-geist-border tw-max-h-full tw-overflow-y-auto">
              {files.map((f) => {
                const active = activeId === f.document_id;
                return (
                  <Clickable
                    key={f.document_id}
                    onClick={() => openFile(f.document_id)}
                    title={f.inv_no || "Invoice"}
                    className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2.5 tw-transition-colors ${
                      active ? "tw-bg-geist-accent/5" : "hover:tw-bg-geist-bg-2"
                    }`}
                  >
                    <i className={`bi bi-file-earmark-pdf tw-text-[13px] tw-flex-shrink-0 ${active ? "tw-text-geist-accent" : "tw-text-geist-fg-3"}`} />
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className="tw-block tw-text-[12px] tw-font-medium tw-text-geist-fg tw-truncate">{f.inv_no || "Invoice"}</span>
                      <span className="tw-block tw-text-[10px] tw-text-geist-fg-3 tw-truncate">
                        {[f.inv_date, money(f.total)].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </Clickable>
                );
              })}
            </div>

            {/* Khung PDF */}
            <div className="tw-flex-1 tw-min-w-0 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm tw-overflow-hidden tw-bg-geist-bg-2" style={{ minHeight: 460 }}>
              <InvoicePreviewFrame url={url} loading={loading} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

InvoiceFilesModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  paymentRequest: PropTypes.shape({
    code: PropTypes.string,
    invoice_files: PropTypes.arrayOf(PropTypes.shape({
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      inv_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      inv_date: PropTypes.string,
      total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
  }),
};

export default InvoiceFilesModal;
